import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import swal from 'sweetalert';
import { useAuth0Context } from './AuthProvider';
import { rolesConfig, components } from '../utils/config';
import history from '../history';
import env from '../constants';

const useIsAuthenticated = expiresAt => useMemo(
  () => new Date().getTime() < expiresAt, [expiresAt],
);


export const useAuth = () => {
  const { i18n } = useTranslation();

  const { auth0, authState, updateAuthState } = useAuth0Context();
  const isAuthenticated = useIsAuthenticated(authState.expiresAt);
  const { t } = useTranslation();

  const login = useCallback(() => {
    auth0.authorize();
  }, [auth0]);


  const logout = useCallback(() => {
    updateAuthState({
      authenticated: false,
      accessToken: null,
      idToken: null,
      name: null,
      profilePicture: null,
      countrySelected: null,
      language: null,
      email: null,
      role: null,
      allowedRoutes: [],
      expiresAt: 0,
    });
    localStorage.removeItem('isLoggedIn');
    auth0.logout({
      returnTo: window.location.origin,
    });
    history.replace('/');
    // eslint-disable-next-line
  }, [auth0, updateAuthState]);

  const setSession = useCallback(
    async (authResult) => {
      localStorage.setItem('isLoggedIn', 'true');
      const expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
      let roles = ['common'];
      let containerAllowed = [];
      let moduleConfigs = {};
      const urlCode = `${env.tenantUrl}/country/code` || 'code';
      let countrySelected = authResult.idTokenPayload[urlCode].toLowerCase();
      if (localStorage.getItem('countrySelected')) {
        countrySelected = localStorage.getItem('countrySelected');
      }
      const { url } = env.allCountries[countrySelected];
      const urlFetch = `${url}/shopper-erp/api/auth/users/authorize`;
      try {
        const response = await axios({
          method: 'post',
          url: urlFetch,
          headers: {
            Authorization: `Bearer ${authResult.accessToken}`,
          },
          data: {
            email: authResult.idTokenPayload.email,
          },
        });
        if (response.data.permissions && Object.keys(response.data.permissions).length) {
          const userPermissions = response.data.permissions;
          moduleConfigs = response.data.modules_config && response.data.modules_config;
          roles = response.data.role;
          containerAllowed = Object.keys(userPermissions).map(
            value => ({
              ...components[value],
              permissions: userPermissions[value],
              config: moduleConfigs[value],
            }),
          );
          let childRoutes = [];
          containerAllowed.forEach((allowedRoute) => {
            if (allowedRoute.children) {
              childRoutes = childRoutes.concat(allowedRoute.children.map(childRoute => ({
                ...childRoute,
                permissions: allowedRoute.permissions,
              })));
            }
          });
          containerAllowed = containerAllowed.concat(childRoutes);
          if (!containerAllowed[containerAllowed.length - 1].component) containerAllowed.pop();
        } else {
          swal({ title: t('Components.Dashboard.ForbiddenCountry'), icon: 'error' });
          containerAllowed = roles.reduce((acc, role) => [...acc, ...rolesConfig[role].routes], []);
        }
      } catch (e) {
        swal({ title: 'Ops!', icon: 'error' });
        history.push('/');
      }

      updateAuthState({
        authenticated: true,
        accessToken: authResult.accessToken,
        idToken: authResult.idToken,
        name: authResult.idTokenPayload.given_name,
        profilePicture: authResult.idTokenPayload.picture,
        countrySelected,
        language: authResult.idTokenPayload.locale,
        email: authResult.idTokenPayload.email,
        role: roles,
        allowedRoutes: containerAllowed,
        moduleConfigs,
        expiresAt,
      });

      localStorage.setItem('countrySelected', countrySelected);
      i18n.changeLanguage(authResult.idTokenPayload.locale);
    },
    // eslint-disable-next-line
    [updateAuthState],
  );

  const renewSession = useCallback(() => {
    auth0.checkSession({}, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        setSession(authResult);
      } else if (err) {
        logout();
      } else {
        history.push('/');
      }
    });
    // eslint-disable-next-line
  }, []);

  const handleAuthentication = useCallback((url) => {
    auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        setSession(authResult);
        history.push(url);
      } else if (err) {
        history.push('/');
      } else {
        history.push('/');
      }
    });
    // eslint-disable-next-line
  }, []);

  return {
    login,
    logout,
    handleAuthentication,
    isAuthenticated,
    renewSession,
    setSession,
  };
};

export default useAuth;
